/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import { format, isValid } from "date-fns";
import { DATE_FORMAT, UAE } from "../../../../../../constants";
import { updateProspect } from "../../../../../../store/actions/appConfig";
import {
  Input,
  AutoSaveField as Field,
  SelectAutocomplete,
  DatePicker
} from "../../../../../../components/Form";

import {
  getRequiredMessage,
  getInvalidMessage,
  getMinDateMessage
} from "../../../../../../utils/getValidationMessage";

import { ButtonWrapper } from "./styled";
import { MAX_FLAT_NUMBER_FIELD_LENGTH, MAX_STREET_NUMBER_LENGTH } from "./constants";
import { SPECIAL_CHARACTERS_REGEX } from "../../../../../../utils/validation";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { FormContainer } from "../SignatoryBankingRelationships/styled";
import { ScrollToError } from "components/ScrollToError";

const currentYear = format(new Date(), "yyyy");
const SignatoryResidenceAddressSchema = (
  isShareholderACompany,
  isFieldsRequired,
  isSignatory,
  residenceCountry
) =>
  Yup.object().shape({
    addressLine1: Yup.string()
      .nullable()
      .test(
        "required",
        getRequiredMessage("Flat number"),
        value => isShareholderACompany || !isFieldsRequired || value
      )
      .max(
        MAX_FLAT_NUMBER_FIELD_LENGTH,
        "Maximum " + MAX_FLAT_NUMBER_FIELD_LENGTH + " characters allowed"
      )
      .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("flat Number")),
    addressLine2: Yup.string()
      .nullable()
      .test(
        "required",
        getRequiredMessage("Building Name"),
        value => isShareholderACompany || !isFieldsRequired || value
      )
      .max(50, "Maximum 50 characters allowed")
      .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Building Name")),
    addressLine3: Yup.string()
      .nullable()
      .max(MAX_STREET_NUMBER_LENGTH, `Maximum ${MAX_STREET_NUMBER_LENGTH} characters allowed`)
      .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Street / Location")),
    addressLine4: Yup.string()
      .nullable()
      .max(50, "Maximum 50 characters allowed")
      .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Landmark")),
    residenceType: Yup.string().nullable(),
    yearsInUAE: Yup.date()
      .nullable()
      .test(
        "required",
        getRequiredMessage("Signatory in UAE since which year"),
        value => (!isSignatory && residenceCountry === UAE) || residenceCountry !== UAE || value
      )
      .min(new Date(1900, 0, 1), getMinDateMessage("Signatory in UAE since which year?"))
      .max(new Date(currentYear, 11, 31), getInvalidMessage("Signatory in UAE since which year?"))
      .typeError(getInvalidMessage("Signatory in UAE since which year?"))
  });
const intialSchema = {
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  residenceType: "",
  addressLine4: "",
  yearsInUAE: "",
  typeOfAddress: "Residence Address"
};
export const SignatoryResidenceAddressComponent = ({
  handleContinue,
  index,
  isShareholderACompany,
  isFieldsRequired,
  stakeholders
}) => {
  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      updateProspect({
        [`prospect.signatoryInfo[${index}].kycDetails.addressInfo[0].typeOfAddress`]: "Residence Address"
      })
    );
  }, [dispatch, index]);
  const basePath = `prospect.signatoryInfo[${index}].kycDetails.addressInfo[0].addressDetails`;
  return (
    <Formik
      initialValues={intialSchema}
      validationSchema={SignatoryResidenceAddressSchema(
        isShareholderACompany,
        isFieldsRequired,
        stakeholders[index].isSignatory,
        stakeholders[index].residenceCountry
      )}
      onSubmit={handleContinue}
      validateOnChange={false}
    >
      {({ values }) => {
        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={6} xs={12}>
                <Field
                  name="addressLine1"
                  path={`${basePath}.addressLine1`}
                  label="Flat Number"
                  component={Input}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="addressLine2"
                  path={`${basePath}.addressLine2`}
                  label="Building Name"
                  component={Input}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="addressLine3"
                  path={`${basePath}.addressLine3`}
                  label="Street / Location"
                  component={Input}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="addressLine4"
                  path={`${basePath}.addressLine4`}
                  label="Landmark"
                  component={Input}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="residenceType"
                  path={`${basePath}.residenceType`}
                  datalistId="residenceType"
                  label="Residence Type"
                  component={SelectAutocomplete}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="yearsInUAE"
                  label="Signatory in UAE since which year"
                  path={`${basePath}.yearsInUAE`}
                  component={DatePicker}
                  openTo="year"
                  views={["year"]}
                  format="yyyy"
                  placeholder="____"
                  disableFuture
                  changeProspect={changeDateProspectHandler}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  maxDate={new Date()}
                />
              </Grid>{" "}
            </Grid>
            <ButtonWrapper>
              <ContinueButton type="submit" />
            </ButtonWrapper>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

SignatoryResidenceAddressComponent.propTypes = {
  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Index of the signatory in the signatories array
   */
  index: PropTypes.number.isRequired,

  /**
   * Flag indicating if the stakeholder is a company
   */
  isShareholderACompany: PropTypes.bool,

  /**
   * Flag indicating if the fields are required for validation
   */
  isFieldsRequired: PropTypes.bool,

  /**
   * Array of stakeholder information objects
   */
  stakeholders: PropTypes.arrayOf(
    PropTypes.shape({
      isSignatory: PropTypes.bool,
      residenceCountry: PropTypes.string,
      fullName: PropTypes.string
    })
  ).isRequired
};
